import FormControl from "common/components/FormControl/FormControl";
import FormScrollError from "common/components/FormScrollError/FormScrollError";
import { useMemo } from "react";
import { Form } from "react-final-form";
import { useTranslation } from "react-i18next";
import arrayMutators from "final-form-arrays";
import useRouter from "common/hooks/use-router";
import EditHeader from "common/components/EditHeader/EditHeader";
import SectionTitle from "common/components/SectionTitle/SectionTitle";
import ToggleSwitch from "common/components/ToggleSwitch/ToggleSwitch";
import HeaderButtonsEdit from "common/components/EditHeader/HeaderButtonsEdit";
import fixFinalFormEmptyValues from "utils/fixFinalFormEmptyValues";
import { Review } from "types/Review";
import { CurrencyEuroIcon, UserIcon } from "@heroicons/react/24/outline";
import { StarIcon } from "@heroicons/react/24/solid";
import ImageComponent from "common/components/ImageComponent/ImageComponent";
import ImageFromStrapiMedia from "common/components/ImageFromStrapiMedia/ImageFromStrapiMedia";
import { Link } from "react-router-dom";
import dayjs from "dayjs";
import Status from "common/components/Status/Status";
import CategorySelect from "components/select/CategorySelect/CategorySelect";
import Icon from "components/Icon/Icon";
import UserSelect from "components/select/UserSelect/UserSelect";
import { Profile } from "types/Profile";
import AddressSelect from "components/select/AddressSelect/AddressSelect";

function ProfileForm({ onSubmit, editedUser: editedProfile, myAccount }: any) {
  const { t } = useTranslation();
  const { navigate } = useRouter();

  const initialValues: Profile = useMemo(
    () =>
      editedProfile?.id
        ? {
            ...editedProfile,
            role: editedProfile?.user?.role?.id ?? 1,
            role_name: editedProfile?.user?.role?.name,
            services: editedProfile?.services?.map(
              (service: any) => service.id
            ),
            address: editedProfile.address?.id,
            user: editedProfile.user?.id,
          }
        : {
            username: undefined,
            phone: "",
            address: "",
            description: "",
            services: [],
            blocked: false,
            confirmed: true,
            isProvider: false,
            isCompany: false,
            verified: false,
          },
    [editedProfile]
  );

  const onSubmitWrapper = async (values: Profile) => {
    const res = await onSubmit(fixFinalFormEmptyValues(initialValues, values));

    return res;
  };

  return (
    <Form
      onSubmit={onSubmitWrapper}
      mutators={{
        ...arrayMutators,
      }}
      subscription={{ values: true, submitting: true, pristine: true }}
      initialValues={initialValues}
      render={({ handleSubmit, values }) => (
        <div className="mx-auto max-w-full py-4 px-4 text-slate-500 text-base">
          {/* Header */}
          <EditHeader
            title={
              myAccount
                ? `${t("titlePages.myAccount")}`
                : t(
                    `common.${
                      editedProfile && editedProfile.id ? "edit" : "add"
                    }`
                  )
            }
            subTitle={
              editedProfile?.id
                ? `${values.username} - ${values.firstname || ""} ${
                    values.lastname || ""
                  }`
                : t("newItem.userLegend")
            }
            icon="UsersIcon"
            HeaderButtonsEdit={
              <HeaderButtonsEdit
                inHeader
                onSubmit={handleSubmit}
                onCancel={myAccount ? null : () => navigate("/profiles")}
              />
            }
          />
          <form onSubmit={handleSubmit} noValidate autoComplete="off">
            <FormScrollError />

            <div className="responsive-flex gap-2 mt-2">
              <div className="md:w-2/3 flex flex-col gap-2">
                <div className="white-box">
                  <div className="responsive-flex gap-4">
                    <div className="flex flex-col w-full md:w-[180px]">
                      <FormControl
                        title={t("forms.avatar")}
                        labelIcon="PhotoIcon"
                        name="avatar"
                        type="image"
                        containerStyle="mb-2"
                      />
                      <div className="text-xs text-center w-full px-1 pt-0 pb-4">
                        {t("forms.subscribedThe", {
                          since: dayjs(values?.createdAt).format("DD/MM/YYYY"),
                        })}
                      </div>

                      {values.isProvider && (
                        <div className="flex items-center gap-1 leading-[0] text-slate-500 shrink-0 font-normal pb-1">
                          <Icon
                            name="Provider"
                            className="w-6 h-6 rounded-full border-2 border-slate-500 p-1 mr-0.5"
                          />
                          {t("common.provider")}
                        </div>
                      )}
                      {values.isCompany && (
                        <div
                          className={`flex items-center gap-1 font-normal leading-[0] ${
                            values?.verified ? "text-green-500" : "text-red-500"
                          } shrink-0 pb-1`}
                        >
                          <Icon
                            name={
                              values.verified
                                ? "CheckCircleIcon"
                                : "XCircleIcon"
                            }
                            className="w-7 h-7 -ml-0.5"
                          />
                          {values?.verified
                            ? t("common.verifiedPro")
                            : t("common.nonVerifiedPro")}
                        </div>
                      )}

                      <FormControl
                        type="custom"
                        name="blocked"
                        containerStyle="my-2"
                      >
                        <ToggleSwitch rightLabel={t("forms.blocked")} />
                      </FormControl>
                    </div>
                    <div className="flex flex-col w-full gap-2">
                      <FormControl
                        name="username"
                        titleKey="forms.username"
                        required
                      />
                      <FormControl
                        name="firstname"
                        titleKey="forms.firstname"
                        required
                      />
                      <FormControl
                        name="lastname"
                        titleKey="forms.lastname"
                        required
                      />
                      <FormControl
                        name="phone"
                        titleKey="forms.phone"
                        required
                      />
                      <FormControl
                        name="address"
                        titleKey="forms.address"
                        type="custom"
                      >
                        <AddressSelect />
                      </FormControl>
                      <FormControl
                        type="textarea"
                        name="companyDescription"
                        titleKey="forms.presentation"
                        className="w-full"
                      />
                    </div>
                  </div>
                </div>
                <div className="white-box flex flex-col gap-4">
                  <div className="responsive-flex ">
                    <FormControl type="custom" name="isProvider">
                      <ToggleSwitch rightLabel={t("forms.isProvider")} />
                    </FormControl>
                  </div>
                  {values.isProvider && (
                    <div className="responsive-flex gap-4">
                      <div className="flex flex-col w-full">
                        <FormControl
                          name="services"
                          titleKey="forms.category"
                          type="custom"
                        >
                          <CategorySelect multiple />
                        </FormControl>
                      </div>
                    </div>
                  )}
                </div>
                {values.isProvider && (
                  <div className="white-box flex flex-col gap-4">
                    <div className="responsive-flex ">
                      <FormControl type="custom" name="isCompany">
                        <ToggleSwitch rightLabel={t("forms.hasCompany")} />
                      </FormControl>
                    </div>
                    {values.isCompany && (
                      <div className="responsive-flex gap-4">
                        <div className="flex flex-col w-full md:w-[180px]">
                          <FormControl
                            title={t("forms.companyLogo")}
                            labelIcon="PhotoIcon"
                            name="companyLogo"
                            type="image"
                          />
                        </div>
                        <div className="flex flex-col w-full gap-4">
                          <FormControl
                            name="companyName"
                            titleKey="forms.companyName"
                            required
                          />
                          <FormControl
                            name="companySiret"
                            titleKey="forms.companySiret"
                            required
                          />
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="flex flex-col md:w-1/3 gap-2">
                <div className="white-box">
                  <div className="flex flex-col w-full gap-2">
                    <SectionTitle
                      title={t("forms.relatedAccount")}
                      icon="CalendarDaysIcon"
                    />

                    <FormControl
                      name="user"
                      titleKey="forms.user"
                      type="custom"
                      disabled={!!values.user}
                    >
                      <UserSelect />
                    </FormControl>
                  </div>
                </div>

                <div className="white-box">
                  <div className="flex flex-col w-full gap-2">
                    <SectionTitle
                      title={t("forms.quotas")}
                      icon="RiNUmbersLine"
                    />
                    <FormControl
                      name="nbFavors"
                      titleKey="forms.favors"
                      containerStyle="grid grid-cols-2 gap-2"
                    />
                    <FormControl
                      name="nbCanContactApplicant"
                      titleKey="forms.contactApplicant"
                      containerStyle="grid grid-cols-2 gap-2"
                    />
                  </div>
                </div>

                <div className="white-box flex flex-col gap-8">
                  <h2 className="flex flex-row justify-between text-lg font-semibold">
                    Note globale :
                    <div className="flex flex-row gap-1 items-center">
                      <StarIcon width={25} className="text-yellow-500" />

                      <p className="text-gray-600 leading-6 text-xl">
                        <b className="text-gray-900">{editedProfile?.rating}</b>
                        /5
                      </p>
                    </div>
                  </h2>
                  {editedProfile?.reviews?.map((review: Review) => {
                    return (
                      <div key={`review-${review.id}`}>
                        <div className="flex flex-row items-start gap-4">
                          <div className="w-12 h-12 rounded-lg overflow-hidden">
                            <ImageComponent
                              image={
                                review.authorProfile?.avatar
                                  ? ImageFromStrapiMedia(
                                      review.authorProfile?.avatar
                                    )?.uri
                                  : undefined
                              }
                            />
                          </div>

                          <div className="flex-1 gap-1">
                            <div className="flex flex-row justify-between gap-1.5">
                              <div className="flex flex-row gap-1 items-center">
                                <StarIcon
                                  width={25}
                                  className="text-yellow-500"
                                />

                                <p className="text-gray-600 leading-6 text-xl">
                                  <b className="text-gray-900">
                                    {review.ratings}
                                  </b>
                                  /5
                                </p>
                              </div>

                              <Status status={review.type} />
                            </div>

                            <p className="mb-1 font-semibold">
                              {review.comment}
                            </p>

                            <p className="mt-2 flex flex-row gap-4">
                              <UserIcon width={24} className="" />
                              <Link
                                to={`/profiles/edit/${review.authorProfile?.id}`}
                              >
                                <span className="border-b-2 border-slate-500 hover:border-emerald-500 text-slate-800">
                                  {review.authorProfile?.username}
                                </span>
                              </Link>
                              , le{" "}
                              {dayjs(review?.createdAt).format("DD/MM/YYYY")}
                            </p>
                            <p className="mt-2 flex flex-row gap-4">
                              <CurrencyEuroIcon width={24} className="" />
                              <Link
                                to={`/conversations/${review.authorProfile?.id}`}
                              >
                                <span className="border-b-2 border-slate-500 hover:border-emerald-500 text-slate-800">
                                  {review.conversation?.content?.title}
                                </span>
                              </Link>
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            <HeaderButtonsEdit
              onSubmit={handleSubmit}
              onCancel={myAccount ? null : () => navigate("/profiles")}
            />
          </form>
        </div>
      )}
    />
  );
}
export default ProfileForm;
